// The left navigation budget.
//
// The shell has two left-hand navigation surfaces -- the workspace sidebar
// (`core/layouts/app.vue`) and the views rail (`database/components/table/
// Table.vue`) -- and they are owned by different components with no channel
// between them. Left independent, their maxima compose: at 1280px an expanded
// sidebar (240) plus an expanded rail (240) plus the right region (400) leaves
// the grid 400px, which is narrower than two default field columns.
//
// This module coordinates the two surfaces when their combined width would
// crowd the content area. Wide layouts permit both to stay expanded; compact
// layouts still give one surface the budget and render the other as a rail.
//
// The rule that makes this safe is the distinction between a *preference* and a
// *forced* collapse, which `app.vue` already draws for the viewport
// (`col1CollapsedPreference` vs `col1Narrow`). A forced collapse must never be
// written to storage: the user asked for an expanded sidebar, and the moment the
// budget allows it again they must get it back. So this module holds no
// preference of its own. It holds only "who currently owns the budget", and each
// component intersects that with the preference it already stores.

import {
  readLocalStorage,
  writeLocalStorage,
  SHELL_LEFT_NAV_EXPANDED_KEY,
} from '@baserow/modules/core/utils/localStorage'
import {
  BAND_WIDE,
  bandForWidth,
  resolvePanelState,
} from '@baserow/modules/core/utils/shellPanelState'
import {
  BREAKPOINT_MD,
  BREAKPOINT_SM,
} from '@baserow/modules/core/utils/breakpoints'

// The two claimants on the budget. `null` is a real, reachable value: it is
// state L3, both collapsed, which the user reaches by collapsing whichever
// surface is expanded. Collapsing must not hand the budget to the other
// surface, or L3 would be unreachable.
export const LEFT_NAV_SIDEBAR = 'sidebar'
export const LEFT_NAV_VIEWS = 'views'

const LEFT_NAV_CLAIMANTS = [LEFT_NAV_SIDEBAR, LEFT_NAV_VIEWS]

// Written when the budget is released, to distinguish "the user chose focus
// mode" from "this browser has never seen Model C". Both read back as "nobody
// holds it", but only the second may be filled in from a surface's own
// preference -- otherwise a reload would quietly undo state L3.
const LEFT_NAV_NONE = 'none'

/**
 * Reads the last-expanded surface. Returns `undefined` when the key has never
 * been written, `null` when it was explicitly released, and otherwise the
 * claimant.
 *
 * Anything unrecognised reads as `undefined`: a stale or hand-edited key must
 * not put the budget in a state neither component can release, and treating it
 * as "never seen" lets the next mount reconcile it from a real preference.
 */
export function readLeftNavExpanded() {
  const stored = readLocalStorage(SHELL_LEFT_NAV_EXPANDED_KEY)
  if (LEFT_NAV_CLAIMANTS.includes(stored)) {
    return stored
  }
  return stored === LEFT_NAV_NONE ? null : undefined
}

export const state = () => ({
  // Which left surface currently owns the expansion budget, or `null` when
  // neither does (focus mode). This is never the source of truth for what the
  // user chose -- each surface still owns its own collapse preference -- only
  // for which of them is currently allowed to act on it.
  //
  // Not seeded from storage here: `state()` runs during store construction,
  // which is also server-side, and `readLocalStorage` guards a `window` that
  // does not exist there. `hydrateLeftNav` does it on mount instead.
  leftNavExpanded: null,
  dualLeftNavigationAllowed: false,

  // The viewport band, and each surface's own collapse preference and overlay
  // state. These used to live as refs inside `app.vue` and data on `Table.vue`,
  // where each component recomputed `narrow || forced || preference` for itself
  // and neither could see the other's answer. See
  // `docs/hypedrive/17-shell-rearchitecture.md` §1.
  //
  // `BAND_WIDE` is the SSR default, matching the 240px `layout.scss` renders
  // before any media query applies. Seeding it from `window.innerWidth` here
  // would put the server and the client on different bands and reproduce the
  // hydration mismatch fixed in 1a4935c6e; `setViewportWidth` runs on mount.
  viewportBand: BAND_WIDE,
  collapsedPreference: {},
  overlayOpen: {},
})

export const mutations = {
  SET_LEFT_NAV_EXPANDED(state, claimant) {
    state.leftNavExpanded = claimant
  },
  SET_DUAL_LEFT_NAVIGATION_ALLOWED(state, allowed) {
    state.dualLeftNavigationAllowed = allowed
  },
  SET_VIEWPORT_BAND(state, band) {
    // An overlay belongs to the band that produced it. Leaving `overlayOpen`
    // set when the band changes means a panel opened on a phone is still
    // flagged open after the viewport widens -- and comes back on its own the
    // next time it narrows, which on a phone is every rotation. The user closes
    // it, turns the device, and it is open again with nothing having asked for
    // it.
    //
    // Dropped rather than remembered: a floating panel is a transient answer to
    // "show me this now", not a preference. The preference the user does have
    // lives in `collapsedPreference` and is untouched here.
    if (state.viewportBand !== band) {
      state.overlayOpen = {}
    }
    state.viewportBand = band
  },
  SET_COLLAPSED_PREFERENCE(state, { panel, collapsed }) {
    state.collapsedPreference = {
      ...state.collapsedPreference,
      [panel]: collapsed,
    }
  },
  SET_OVERLAY_OPEN(state, { panel, open }) {
    state.overlayOpen = { ...state.overlayOpen, [panel]: open }
  },
}

export const actions = {
  /**
   * Claims the left navigation budget for `claimant`, evicting whoever held it.
   *
   * This is the whole state machine:
   *
   *   claim('sidebar') -> sidebar owns it, views rail is forced to a rail  (L1)
   *   claim('views')   -> views rail owns it, sidebar is forced to a rail  (L2)
   *   release(x)       -> nobody owns it, both are rails                   (L3)
   *
   * Note what is absent: `release` does not promote the other claimant. That
   * asymmetry is the requirement -- expanding one collapses the other, but
   * collapsing one must leave the other collapsed, or focus mode could never be
   * entered and would never stick.
   */
  claimLeftNav({ commit }, claimant) {
    commit('SET_LEFT_NAV_EXPANDED', claimant)
    writeLocalStorage(SHELL_LEFT_NAV_EXPANDED_KEY, claimant)
  },
  setDualLeftNavigationAllowed({ commit }, allowed) {
    commit('SET_DUAL_LEFT_NAVIGATION_ALLOWED', allowed)
  },
  /**
   * Records the viewport band. Called on mount and on every crossing of a
   * breakpoint, never during setup -- see the note on `viewportBand` in
   * `state()`.
   */
  setViewportWidth({ commit }, width) {
    commit(
      'SET_VIEWPORT_BAND',
      bandForWidth(width, { sm: BREAKPOINT_SM, md: BREAKPOINT_MD })
    )
  },
  /**
   * Records what the user chose for a panel.
   *
   * Storage is the caller's business: each surface already owns its own key
   * (`SHELL_LEFT_COLLAPSED_KEY`, `baserow.views.panelCollapsed`), and the rule
   * that a *forced* collapse must never be written applies to whoever holds the
   * key. This module holds the value only so the resolver can read it.
   */
  setCollapsedPreference({ commit }, { panel, collapsed }) {
    commit('SET_COLLAPSED_PREFERENCE', { panel, collapsed })
  },
  /**
   * Opens or closes a panel's compact-band overlay.
   *
   * Only meaningful below `sm`, and the resolver enforces that -- an overlay
   * left open while the window widens must not leak into the wide band, where
   * it would be a floating panel over content that has room for a column.
   */
  setOverlayOpen({ commit, state }, { panel, open }) {
    // One overlay at a time. Both panels float from the left edge at the same
    // width and on the same z-index rung, so a second one opened over the first
    // covers it exactly -- and dismissing what you see leaves the other still
    // open underneath, so the user taps the scrim twice for a panel they only
    // opened once. Opening either therefore closes the other, which is what
    // "modal surface" means for two surfaces that occupy the same space.
    if (open) {
      Object.keys(state.overlayOpen).forEach((other) => {
        if (other !== panel && state.overlayOpen[other]) {
          commit('SET_OVERLAY_OPEN', { panel: other, open: false })
        }
      })
    }
    commit('SET_OVERLAY_OPEN', { panel, open })
  },
  /**
   * Gives up the budget, but only if `claimant` is the one currently holding
   * it. The guard matters because both surfaces report their collapse state
   * independently: a views rail that collapses while the sidebar owns the
   * budget must not evict the sidebar.
   */
  releaseLeftNav({ commit, state }, claimant) {
    if (state.leftNavExpanded === claimant) {
      commit('SET_LEFT_NAV_EXPANDED', null)
      writeLocalStorage(SHELL_LEFT_NAV_EXPANDED_KEY, LEFT_NAV_NONE)
    }
  },
  /**
   * Restores the last-expanded surface on mount.
   *
   * Both surfaces call this and whichever mounts first wins, which is safe
   * because it only ever runs while the budget is untouched: a second hydrate
   * that overwrote a live claim would undo a toggle the user had already made.
   * `app.vue` mounts before `Table.vue`, but neither can rely on that.
   *
   * `fallback` is the caller's own claim, used only on a browser that has never
   * stored a holder. That case is a real one -- every existing user is in it on
   * first load after this ships, with two independent preferences that may both
   * say expanded. Someone has to win, and the surface asking is the one that
   * knows its preference is expanded. It is deliberately *not* applied when the
   * key says the budget was explicitly released, or reloading would silently
   * leave focus mode.
   */
  hydrateLeftNav({ commit, state }, fallback = null) {
    if (state.leftNavExpanded !== null) {
      return
    }
    const stored = readLeftNavExpanded()
    if (stored !== undefined) {
      commit('SET_LEFT_NAV_EXPANDED', stored)
    } else if (fallback !== null) {
      commit('SET_LEFT_NAV_EXPANDED', fallback)
      writeLocalStorage(SHELL_LEFT_NAV_EXPANDED_KEY, fallback)
    }
  },
}

export const getters = {
  /**
   * True when `claimant` may not currently be expanded.
   *
   * The obvious reading -- "the other surface holds the budget" -- is not quite
   * enough. It leaves state L3 able to show both surfaces expanded, because
   * each surface still holds its own preference and both may say expanded: two
   * independent preferences existed before this model did, and every user
   * upgrading into it arrives with whatever pair they had. `leftNavExpanded`
   * is `null` in L3, so a getter keyed only on the other surface would force
   * neither, and the two stale preferences would both win.
   *
   * So the rule is the invariant itself: a surface is allowed to be expanded
   * exactly while it holds the budget. Every legitimate expansion goes through
   * `claimLeftNav`, so this costs a correct expansion nothing -- and it is what
   * keeps the model from ever having to write a preference it does not own.
   */
  isLeftNavForcedCollapsed: (state) => (claimant) => {
    return (
      !state.dualLeftNavigationAllowed && state.leftNavExpanded !== claimant
    )
  },
  leftNavExpanded(state) {
    return state.leftNavExpanded
  },
  viewportBand(state) {
    return state.viewportBand
  },
  /**
   * The resolved state of a left-region panel: one of `PANEL_*`.
   *
   * This is the single answer the two surfaces used to compute separately. Both
   * inputs it does not hold itself -- the band and the preference -- are in this
   * module now, so there is nothing left for a component to combine.
   */
  panelState: (state, getters) => (panel) => {
    return resolvePanelState(state.viewportBand, {
      collapsedPreference: state.collapsedPreference[panel] === true,
      forcedByBudget: getters.isLeftNavForcedCollapsed(panel),
      overlayOpen: state.overlayOpen[panel] === true,
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
